import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Layout } from '../../components/layout/Layout';
import { testimonialsAPI } from '../../lib/api';
import { 
    MapPin, 
    Users, 
    Briefcase, 
    CheckCircle2, 
    ArrowRight,
    Star,
    TrendingUp,
    Shield,
    ChevronLeft,
    ChevronRight as ChevronRightIcon,
    Building2,
    Sparkles,
    Clock,
    Zap,
    ChevronDown,
    HelpCircle
} from 'lucide-react';

// Hero slider images with text content
const heroSlides = [
    {
        image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&h=1080&fit=crop',
        title: 'Stop Searching. Start Getting Hired.',
        subtitle: 'Join 10,000+ Job Seekers',
        description: 'Connect with 4500+ UK & Canada recruitment agencies. Let experts find opportunities you\'d never discover alone.'
    },
    {
        image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1920&h=1080&fit=crop',
        title: 'Tired of Applying & Getting Ghosted?',
        subtitle: 'We Apply For You',
        description: 'Our team submits 100-250 tailored applications on your behalf. More applications = More interviews = Your dream job.'
    },
    {
        image: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=1920&h=1080&fit=crop',
        title: 'We Apply. You Interview. You Get Hired.',
        subtitle: 'Multi-Apply Service',
        description: 'While you sleep, we\'re applying. While you work, we\'re matching. While you wait, interviews are coming.'
    },
    {
        image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1920&h=1080&fit=crop',
        title: 'Your Next Chapter Starts Here',
        subtitle: '95% Satisfaction Rate',
        description: 'Join thousands who landed their dream roles through RecruitNearMe. Your success story is waiting.'
    }
];

// Default testimonials (fallback if none in database)
const defaultTestimonials = [
    {
        testimonial_id: 'default_1',
        name: 'Sarah Thompson',
        role: 'Marketing Manager',
        location: 'London',
        image_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
        quote: 'I was applying to 20+ jobs a week with zero responses. After 2 weeks with Multi-Apply, I had 5 interviews and 2 offers. Now I\'m at my dream company earning £15k more than before!',
        rating: 5
    },
    {
        testimonial_id: 'default_2',
        name: 'James Mitchell',
        role: 'Software Developer',
        location: 'Manchester',
        image_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        quote: 'The Pro+ package changed everything. 250 tailored applications while I focused on interview prep. Got multiple offers and negotiated a 30% salary increase. Worth every penny!',
        rating: 5
    },
    {
        testimonial_id: 'default_3',
        name: 'Priya Patel',
        role: 'Finance Analyst',
        location: 'Birmingham',
        image_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
        quote: 'Skeptical at first, but within 3 weeks I went from unemployed to choosing between 3 job offers. The team found roles I never would have discovered on my own. Life-changing!',
        rating: 5
    }
];

const HomePage = () => {
    const { login, isAuthenticated, user } = useAuth();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [testimonials, setTestimonials] = useState(defaultTestimonials);
    const [openFaq, setOpenFaq] = useState(null);

    // FAQ data
    const faqs = [
        {
            question: "How does the Multi-Apply service work?",
            answer: "Once you sign up, you'll share your CV, target roles, and preferences. Our team then crafts tailored applications for each job - including customised cover letters - and submits them on your behalf. You can track all applications in real-time through your dashboard."
        },
        {
            question: "How many applications will you submit for me?",
            answer: "Depending on your plan: Starter gets 100 applications/month, Pro gets 250 applications/month, and Pro+ gets 250 applications/month for 3 months. Each application is individually tailored - we never use generic templates."
        },
        {
            question: "How quickly will I start getting interviews?",
            answer: "Most users see their first interview invitations within 1-2 weeks. Results vary based on your experience, target roles, and the job market, but our 95% satisfaction rate speaks for itself."
        },
        {
            question: "Can I cancel Multi-Apply anytime?",
            answer: "Absolutely! There are no long-term contracts. You can cancel your Multi-Apply plan at any time directly from your dashboard. Your access continues until the end of your billing period."
        },
        {
            question: "What makes RecruitNearMe different from job boards?",
            answer: "Unlike job boards where you do all the work, we handle the heavy lifting. Our human team (not AI) writes each application, our agency database includes exclusive recruiters you won't find elsewhere, and you get direct connections rather than competing in massive applicant pools."
        },
        {
            question: "Do I need to pay to browse recruitment agencies?",
            answer: "No. Find Recruiters and Agency Jobs are completely free. Just create a free account to see full contact details and apply directly. Only Multi-Apply is a paid service."
        }
    ];

    // Fetch testimonials from API
    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                const response = await testimonialsAPI.list();
                if (response.data.testimonials && response.data.testimonials.length > 0) {
                    setTestimonials(response.data.testimonials);
                }
            } catch (error) {
                console.log('Using default testimonials');
            }
        };
        fetchTestimonials();
    }, []);

    // Auto-advance slides
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    };

    const features = [
        {
            icon: MapPin,
            title: 'Find Hidden Opportunities',
            description: '4500+ UK & Canada agencies you won\'t find elsewhere. Exclusive roles, direct connections, faster responses.'
        },
        {
            icon: Briefcase,
            title: 'We Apply, You Interview',
            description: 'Stop wasting hours on applications. Our team submits 100-250 tailored applications while you focus on what matters.'
        },
        {
            icon: Users,
            title: '1-on-1 Career Coaching',
            description: 'CV reviews, interview prep, salary negotiation. Get expert guidance that lands you better offers.'
        }
    ];

    const stats = [
        { value: '4500+', label: 'UK & Canada Agencies' },
        { value: '10,000+', label: 'Applications Sent' },
        { value: '95%', label: 'Would Recommend' },
        { value: '1-2 weeks', label: 'Avg. Time to Interview' }
    ];

    return (
        <Layout>
            {/* Hero Slider Section */}
            <section className="relative h-[600px] md:h-[700px] overflow-hidden">
                {/* Slides */}
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-1000 ${
                            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                        }`}
                    >
                        {/* Background Image */}
                        <div 
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                            style={{ backgroundImage: `url(${slide.image})` }}
                        />
                        {/* Dark Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
                        
                        {/* Content */}
                        <div className="relative z-20 h-full flex items-center">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                                <div className="max-w-2xl">
                                    <div className={`transition-all duration-700 delay-200 ${
                                        index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                                    }`}>
                                        <span className="inline-block px-4 py-2 rounded-full bg-primary-600/90 text-white text-sm font-medium mb-6">
                                            {slide.subtitle}
                                        </span>
                                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                                            {slide.title}
                                        </h1>
                                        <p className="text-lg sm:text-xl text-neutral-200 mb-8 max-w-xl">
                                            {slide.description}
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <Button 
                                                size="lg" 
                                                className="h-14 px-8 text-lg bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl transition-all"
                                                asChild
                                            >
                                                <Link to="/multi-apply" data-testid="hero-multi-apply-btn">
                                                    <Sparkles className="w-5 h-5 mr-2" />
                                                    Start Getting Interviews
                                                </Link>
                                            </Button>
                                            <Button 
                                                size="lg" 
                                                variant="outline"
                                                className="h-14 px-8 text-lg border-white/50 text-white hover:bg-white/10 bg-white/5"
                                                asChild
                                            >
                                                <Link to="/agencies" data-testid="hero-find-recruiters-btn">
                                                    <MapPin className="w-5 h-5 mr-2" />
                                                    Browse Recruiters
                                                </Link>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Navigation Arrows */}
                <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
                    aria-label="Previous slide"
                >
                    <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
                    aria-label="Next slide"
                >
                    <ChevronRightIcon className="w-6 h-6 text-white" />
                </button>

                {/* Slide Indicators */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`transition-all duration-300 ${
                                index === currentSlide 
                                    ? 'w-10 h-3 bg-primary-500 rounded-full' 
                                    : 'w-3 h-3 bg-white/50 hover:bg-white/70 rounded-full'
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>

            </section>

            {/* Stats Section */}
            <section className="bg-white border-b border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, idx) => (
                            <div key={idx} className="text-center">
                                <p className="font-heading text-3xl md:text-4xl font-bold text-primary-600">
                                    {stat.value}
                                </p>
                                <p className="text-sm text-neutral-500 mt-1">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="bg-neutral-50 py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
                            Why Job Seekers Choose Us
                        </span>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                            Stop Struggling. Start Succeeding.
                        </h2>
                        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                            The job market is brutal. We level the playing field with tools and services 
                            that give you an unfair advantage.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, idx) => (
                            <div 
                                key={idx}
                                className="bg-white rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300 card-hover"
                            >
                                <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center mb-6">
                                    <feature.icon className="w-7 h-7 text-primary-600" />
                                </div>
                                <h3 className="font-heading text-xl font-semibold text-neutral-900 mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-neutral-600 leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Recruiters Section */}
            <section className="bg-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <span className="inline-block px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                            Find Recruiters Near Me
                        </span>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                            Featured Recruitment Agencies
                        </h2>
                        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                            Connect with top-rated recruitment agencies across the UK
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                name: 'TechRecruit UK',
                                location: 'London',
                                specialty: 'Technology & IT',
                                roles: ['Software Engineers', 'Data Scientists', 'DevOps'],
                                rating: 4.8
                            },
                            {
                                name: 'FinanceFirst Recruitment',
                                location: 'Manchester',
                                specialty: 'Finance & Banking',
                                roles: ['Accountants', 'Financial Analysts', 'Risk Managers'],
                                rating: 4.9
                            },
                            {
                                name: 'Healthcare Connect',
                                location: 'Birmingham',
                                specialty: 'Healthcare & NHS',
                                roles: ['Nurses', 'Doctors', 'Care Workers'],
                                rating: 4.7
                            },
                            {
                                name: 'Creative Talent Agency',
                                location: 'Bristol',
                                specialty: 'Marketing & Creative',
                                roles: ['Designers', 'Marketing Managers', 'Content Writers'],
                                rating: 4.6
                            },
                            {
                                name: 'Engineering Solutions',
                                location: 'Leeds',
                                specialty: 'Engineering',
                                roles: ['Mechanical Engineers', 'Civil Engineers', 'Project Managers'],
                                rating: 4.8
                            },
                            {
                                name: 'Retail & Hospitality Plus',
                                location: 'Edinburgh',
                                specialty: 'Retail & Hospitality',
                                roles: ['Store Managers', 'Hotel Staff', 'Chefs'],
                                rating: 4.5
                            }
                        ].map((agency, idx) => (
                            <div 
                                key={idx}
                                className="bg-neutral-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-neutral-100 hover:border-primary-200"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                                        <Building2 className="w-6 h-6 text-primary-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className={`font-semibold text-neutral-900 ${!isAuthenticated ? 'blur-sm select-none' : ''}`}>{agency.name}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <MapPin className="w-3.5 h-3.5 text-neutral-400" />
                                            <span className="text-sm text-neutral-500">{agency.location}</span>
                                            <span className="text-neutral-300">•</span>
                                            <div className="flex items-center gap-1">
                                                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                                <span className="text-sm font-medium text-neutral-700">{agency.rating}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded">
                                        {agency.specialty}
                                    </span>
                                    <div className="flex flex-wrap gap-1 mt-3">
                                        {agency.roles.map((role) => (
                                            <span key={role} className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded">
                                                {role}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-10">
                        <Button 
                            size="lg"
                            className="bg-primary-600 hover:bg-primary-700"
                            asChild
                        >
                            <Link to="/agencies">
                                <MapPin className="w-5 h-5 mr-2" />
                                View All Recruiters
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="bg-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                            Simple 3-Step Process
                        </span>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                            From Struggling to Hired in Weeks
                        </h2>
                        <p className="text-lg text-neutral-600">
                            No more endless scrolling. No more ghosted applications.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                step: '01',
                                title: 'Tell Us Your Goals',
                                description: 'Share your dream role, salary expectations, and preferred locations. Takes just 5 minutes.'
                            },
                            {
                                step: '02',
                                title: 'We Get to Work',
                                description: 'Our team crafts tailored applications and submits them to matching opportunities. You relax.'
                            },
                            {
                                step: '03',
                                title: 'Interview & Get Hired',
                                description: 'Watch interviews roll in. Accept the offer you deserve. Celebrate your new career.'
                            }
                        ].map((item, idx) => (
                            <div key={idx} className="relative">
                                <div className="absolute -top-4 -left-4 w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                                    <span className="font-heading text-2xl font-bold text-primary-600">{item.step}</span>
                                </div>
                                <div className="pt-8 pl-8">
                                    <h3 className="font-heading text-xl font-semibold text-neutral-900 mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-neutral-600 leading-relaxed">
                                        {item.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Us Section */}
            <section className="bg-neutral-50 py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Image */}
                        <div className="relative">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_f7a92b99-555f-4e55-b62d-f67f76ad29a3/artifacts/i3shfjnz_recruitnearme%20about%20us.webp"
                                alt="RecruitNearMe Team - Celebrating Success Together"
                                className="rounded-2xl shadow-xl w-full h-auto object-cover"
                            />
                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary-600 rounded-2xl -z-10"></div>
                        </div>
                        
                        {/* Content */}
                        <div>
                            <span className="inline-block px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                                About Us
                            </span>
                            <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
                                Connecting Talent with Opportunity
                            </h2>
                            <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                                RecruitNearMe connects job seekers with trusted recruitment agencies near them. We make it easy to discover recruiters by location and industry, submit your CV with confidence, and access hands-on support for applications and interviews.
                            </p>
                            <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
                                Our goal is simple: remove barriers between talent and opportunity. Whether you&apos;re actively job hunting or preparing for your next move, RecruitNearMe gives you clarity, support, and real human help where it matters most.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                    <span className="text-neutral-700">Trusted by thousands</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                    <span className="text-neutral-700">Human-first approach</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                                    <span className="text-neutral-700">UK-wide coverage</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="bg-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        {/* Trustpilot-style header */}
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="w-8 h-8 bg-[#00b67a] flex items-center justify-center">
                                        <Star className="w-5 h-5 fill-white text-white" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <p className="text-sm text-neutral-500 mb-2">Rated <span className="font-semibold text-neutral-700">Excellent</span> • 95% would recommend</p>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                            Real People. Real Results.
                        </h2>
                        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                            Don't just take our word for it. Here's what our community says about their experience.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, idx) => (
                            <div 
                                key={testimonial.testimonial_id || idx}
                                className="bg-neutral-50 rounded-2xl p-8 relative"
                            >
                                {/* Quote mark */}
                                <div className="absolute top-6 right-6 text-6xl text-primary-200 font-serif leading-none">
                                    &ldquo;
                                </div>
                                
                                {/* Trustpilot-style Stars */}
                                <div className="flex gap-0.5 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <div 
                                            key={i} 
                                            className={`w-7 h-7 flex items-center justify-center ${
                                                i < (testimonial.rating || 5) 
                                                    ? 'bg-[#00b67a]' 
                                                    : 'bg-[#dcdce6]'
                                            }`}
                                        >
                                            <Star className="w-4 h-4 fill-white text-white" />
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Quote */}
                                <p className="text-neutral-600 mb-6 leading-relaxed relative z-10">
                                    {testimonial.quote}
                                </p>
                                
                                {/* Author */}
                                <div className="flex items-center gap-4">
                                    {(testimonial.image_url || testimonial.image) ? (
                                        <img 
                                            src={
                                                testimonial.image_url 
                                                    ? (testimonial.image_url.startsWith('/api') 
                                                        ? `${process.env.REACT_APP_BACKEND_URL}${testimonial.image_url}`
                                                        : testimonial.image_url)
                                                    : testimonial.image
                                            }
                                            alt={testimonial.name}
                                            className="w-12 h-12 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                                            <span className="text-primary-600 font-semibold text-lg">
                                                {testimonial.name?.charAt(0)?.toUpperCase() || '?'}
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold text-neutral-900">{testimonial.name}</p>
                                        <p className="text-sm text-neutral-500">{testimonial.role} • {testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="bg-neutral-50 py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                            <HelpCircle className="w-4 h-4" />
                            Got Questions?
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-neutral-600">
                            Everything you need to know about our services
                        </p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl border border-neutral-200 overflow-hidden"
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                    className="w-full flex items-center justify-between p-5 text-left hover:bg-neutral-50 transition-colors"
                                >
                                    <span className="font-medium text-neutral-900 pr-4">{faq.question}</span>
                                    <ChevronDown 
                                        className={`w-5 h-5 text-neutral-500 shrink-0 transition-transform ${
                                            openFaq === index ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>
                                {openFaq === index && (
                                    <div className="px-5 pb-5 pt-0">
                                        <p className="text-neutral-600 leading-relaxed">{faq.answer}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-10">
                        <p className="text-neutral-600 mb-4">Still have questions?</p>
                        <Button variant="outline" asChild>
                            <a href="https://wa.me/447301893140" target="_blank" rel="noopener noreferrer">
                                Chat with us on WhatsApp
                            </a>
                        </Button>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 py-20 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
                </div>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                        Your Dream Job Won't Wait.<br />Neither Should You.
                    </h2>
                    <p className="text-lg md:text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
                        Every day without action is a missed opportunity. Start today and get interviews within weeks, not months.
                    </p>
                    
                    {/* Value proposition */}
                    <div className="flex flex-wrap justify-center gap-6 mb-10">
                        <div className="flex items-center gap-2 text-white">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            <span>No long-term contracts</span>
                        </div>
                        <div className="flex items-center gap-2 text-white">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            <span>Cancel anytime</span>
                        </div>
                        <div className="flex items-center gap-2 text-white">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            <span>Results in 1-2 weeks</span>
                        </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Button 
                            size="lg" 
                            className="h-14 px-10 text-lg bg-white text-primary-700 hover:bg-neutral-100 shadow-xl hover:shadow-2xl transition-all font-semibold"
                            asChild
                        >
                            <Link to="/multi-apply" data-testid="cta-multi-apply-btn">
                                <Sparkles className="w-5 h-5 mr-2" />
                                Start Multi-Apply Now
                            </Link>
                        </Button>
                        {!isAuthenticated && (
                            <Button 
                                size="lg" 
                                variant="outline"
                                className="h-14 px-8 text-lg border-white/30 text-white hover:bg-white/10"
                                onClick={login}
                                data-testid="cta-signup-btn"
                            >
                                Create Free Account
                            </Button>
                        )}
                    </div>
                    
                    <p className="text-sm text-primary-200 mt-6">
                        Join 10,000+ job seekers who transformed their careers
                    </p>
                </div>
            </section>

            {/* Trust Section */}
            <section className="bg-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
                        <div className="flex items-center gap-3">
                            <Shield className="w-8 h-8 text-primary-600" />
                            <div>
                                <p className="font-semibold text-neutral-900">GDPR Compliant</p>
                                <p className="text-sm text-neutral-500">Your data is protected</p>
                            </div>
                        </div>
                        <div className="hidden md:block w-px h-12 bg-neutral-200"></div>
                        <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                            <div>
                                <p className="font-semibold text-neutral-900">Verified Agencies</p>
                                <p className="text-sm text-neutral-500">All agencies are vetted</p>
                            </div>
                        </div>
                        <div className="hidden md:block w-px h-12 bg-neutral-200"></div>
                        <div className="flex items-center gap-3">
                            <Star className="w-8 h-8 text-amber-500" />
                            <div>
                                <p className="font-semibold text-neutral-900">4.9/5 Rating</p>
                                <p className="text-sm text-neutral-500">From 2,000+ reviews</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default HomePage;
